<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { useIncidentStore } from '@/stores/incident'
import { useOpsStore } from '@/stores/ops'
import { useSubjectsStore } from '@/stores/subjects'
import { useAuthStore } from '@/stores/auth'

defineProps<{ incidentId: string }>()
defineEmits<{ next: [] }>()

const incidentStore = useIncidentStore()
const opsStore = useOpsStore()
const subjectsStore = useSubjectsStore()
const authStore = useAuthStore()

const incident = computed(() => incidentStore.currentIncident)
const op = computed(() => opsStore.selectedOp)
const subject = computed(() => subjectsStore.subjects[0] ?? null)

const agencyLogoUrl = ref<string | null>(null)

onMounted(async () => {
  const agencyId = authStore.userProfile?.agency_id
  if (!agencyId) return
  try {
    const snap = await getDoc(doc(db, 'agencies', agencyId))
    if (snap.exists()) {
      agencyLogoUrl.value = (snap.data().agency_logo as string | null) ?? null
    }
  } catch {
    // Logo is optional — ignore fetch errors
  }
})

function formatDateRange() {
  const s = op.value
  if (!s) return '—'
  const start = s.op_start_date && s.op_start_time ? `${s.op_start_date} ${s.op_start_time}` : s.op_start_date ?? null
  const end = s.op_end_date && s.op_end_time ? `${s.op_end_date} ${s.op_end_time}` : s.op_end_date ?? null
  if (start && end) return `${start} — ${end}`
  if (start) return `From: ${start}`
  if (end) return `To: ${end}`
  return s.date ?? '—'
}
</script>

<template>
  <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
    <!-- Section head -->
    <div class="flex items-end gap-3.5 flex-wrap">
      <div class="flex flex-col gap-0.5">
        <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">Cover · Auto-generated</span>
        <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Cover Page</h2>
      </div>
      <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Auto-populated from incident settings and subject data. Read-only.</p>
    </div>

    <!-- Cover preview card -->
    <div class="bg-surface border border-border rounded flex flex-col">
      <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Preview</h3>
        <span class="ml-auto font-mono text-[9px] tracking-[0.08em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low">Read-only</span>
      </div>
      <div class="p-8 space-y-6">
        <!-- Agency logo -->
        <div class="flex justify-center">
          <img
            v-if="agencyLogoUrl"
            :src="agencyLogoUrl"
            class="h-16 object-contain"
            alt="Agency logo"
          />
        </div>

        <div class="text-center space-y-2">
          <h1 class="font-headline font-black text-2xl uppercase tracking-tight text-on-surface">
            Incident Action Plan
          </h1>
          <p class="font-mono text-sm text-outline">
            Operational Period {{ op?.op_number ?? '—' }}
          </p>
        </div>

        <div class="border-t border-border pt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p class="text-[10px] font-mono tracking-[0.12em] uppercase text-outline mb-1">Incident Name</p>
            <p class="font-semibold text-on-surface">{{ incident?.incident_name ?? '—' }}</p>
          </div>
          <div>
            <p class="text-[10px] font-mono tracking-[0.12em] uppercase text-outline mb-1">Incident Number</p>
            <p class="font-semibold text-on-surface">{{ incident?.incident_number ?? '—' }}</p>
          </div>
          <div>
            <p class="text-[10px] font-mono tracking-[0.12em] uppercase text-outline mb-1">Operational Period</p>
            <p class="font-semibold text-on-surface">{{ formatDateRange() }}</p>
          </div>
          <div>
            <p class="text-[10px] font-mono tracking-[0.12em] uppercase text-outline mb-1">ICP Location</p>
            <p class="font-semibold text-on-surface">{{ op?.icp_location ?? '—' }}</p>
          </div>
        </div>

        <!-- Subject info -->
        <div v-if="subject" class="border-t border-border pt-4 flex items-center gap-4">
          <img
            :src="subject.photo_url ?? '/assets/subject-placeholder.svg'"
            class="w-16 h-16 rounded object-cover border border-border shrink-0"
            alt="Subject photo"
            @error="($event.target as HTMLImageElement).src = '/assets/subject-placeholder.svg'"
          />
          <div>
            <p class="text-[10px] font-mono tracking-[0.12em] uppercase text-outline mb-1">Subject</p>
            <p class="font-semibold text-on-surface">{{ subject.first_name }} {{ subject.last_name }}</p>
            <p class="text-xs text-outline">{{ subject.age ? `Age ${subject.age}` : '' }} {{ subject.gender }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section footer -->
    <div class="flex items-center gap-2.5 mt-2 pt-3 border-t border-border/60">
      <span class="font-mono text-[10px] tracking-[0.08em] uppercase text-outline">Auto-generated from incident settings</span>
      <span class="flex-1" />
      <button
        class="inline-flex items-center gap-1.5 bg-primary border border-primary rounded px-3.5 py-2 font-mono font-medium text-[11px] tracking-[0.10em] uppercase text-white cursor-pointer hover:opacity-90 transition-opacity"
        @click="$emit('next')"
      >
        ICS 202
        <span class="material-symbols-outlined text-[14px]">arrow_forward</span>
      </button>
    </div>
  </div>
</template>
